import { HTTPError } from 'ky';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { ErrorCode, type ApiErrorEnvelope } from '@bookclub/shared';
import { buildAuthHref, rememberAuthIntent } from '@/features/auth/auth-intent';
import { resolveAccountEmail } from '@/features/auth/account-email';
import { buildClubHref, fetchPaywallOffer } from '@/features/catalog/catalog.api';
import { activeClubContentFreshnessOptions } from '@/features/catalog/catalog.query';
import { useAuthStore } from '@/features/auth/store';
import { buildLegalHref } from '@/features/legal/legal-navigation';
import { activateClubPromoCode, createClubPurchaseOrder } from '@/features/payments/payments.api';
import {
  rememberPurchaseAttempt,
  submitHostedPaymentSession,
} from '@/features/payments/payment-session';
import { useTelegramBackButton } from '@/telegram';
import { ClubHero } from '@/shared/components/club/ClubHero';
import { ClubPurchaseInfo } from '@/shared/components/club/ClubPurchaseInfo';
import { ErrorState } from '@/shared/components/ui/ErrorState';
import { MiniappSurface } from '@/shared/components/ui/MiniappSurface';
import { NeutralLoadingState } from '@/shared/components/ui/NeutralLoadingState';
import { PageFrame } from '@/shared/components/ui/PageFrame';
import { PrimaryButton } from '@/shared/components/ui/PrimaryButton';
import { StackHeader } from '@/shared/components/ui/StackHeader';
import { trackYandexGoal } from '@/shared/lib/yandex-metrica';

function getDemoContext(searchParams: URLSearchParams) {
  return searchParams.get('demo') === 'reader' ? 'demo_reader' : null;
}

const PROMO_CODE_FORMAT_MESSAGE =
  'Промокод может быть до 40 символов и не должен содержать пробелы.';

const PROMO_ACTIVATION_ERROR_MESSAGES: Partial<Record<string, string>> = {
  [ErrorCode.PROMO_CODE_INVALID]: 'Промокод не найден или введён неверно.',
  [ErrorCode.PROMO_CODE_EXPIRED]: 'Срок действия промокода истёк.',
  [ErrorCode.PROMO_CODE_LIMIT_REACHED]: 'Лимит активаций промокода уже исчерпан.',
  [ErrorCode.PROMO_CODE_WRONG_CLUB]: 'Этот промокод не действует для выбранного клуба.',
  [ErrorCode.VALIDATION_ERROR]: PROMO_CODE_FORMAT_MESSAGE,
};

function mapPromoActivationMessage(code?: string, message?: string) {
  if (code && PROMO_ACTIVATION_ERROR_MESSAGES[code]) {
    return PROMO_ACTIVATION_ERROR_MESSAGES[code];
  }

  if (!message) {
    return null;
  }

  const normalized = message.toLowerCase();
  if (normalized.includes('promo code is invalid') || normalized.includes('promo_code_invalid')) {
    return PROMO_ACTIVATION_ERROR_MESSAGES[ErrorCode.PROMO_CODE_INVALID] ?? null;
  }
  if (normalized.includes('promo code is expired')) {
    return PROMO_ACTIVATION_ERROR_MESSAGES[ErrorCode.PROMO_CODE_EXPIRED] ?? null;
  }
  if (normalized.includes('activation limit')) {
    return PROMO_ACTIVATION_ERROR_MESSAGES[ErrorCode.PROMO_CODE_LIMIT_REACHED] ?? null;
  }
  if (normalized.includes('not applicable to this club') || normalized.includes('wrong_club')) {
    return PROMO_ACTIVATION_ERROR_MESSAGES[ErrorCode.PROMO_CODE_WRONG_CLUB] ?? null;
  }
  if (normalized.includes('must not contain spaces') || normalized.includes('must be at most 40')) {
    return PROMO_CODE_FORMAT_MESSAGE;
  }

  return null;
}

async function readApiErrorEnvelope(error: HTTPError) {
  try {
    return (await error.response.clone().json()) as ApiErrorEnvelope;
  } catch {
    return null;
  }
}

async function formatPaymentError(error: unknown) {
  if (error instanceof HTTPError) {
    try {
      const response = (await error.response.clone().json()) as {
        error?: {
          code?: string;
          message?: string;
        };
      };

      if (response.error?.message) {
        return response.error.message;
      }
    } catch {
      // Ignore malformed error payloads and fall back to generic handling.
    }

    if (error.response.status >= 500) {
      return 'Платёжный сервис временно недоступен. Попробуйте ещё раз.';
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'Не удалось подготовить оплату. Попробуйте ещё раз.';
}

async function formatPromoActivationError(error: unknown) {
  if (error instanceof HTTPError) {
    const payload = await readApiErrorEnvelope(error);
    const mappedMessage = mapPromoActivationMessage(payload?.error.code, payload?.error.message);
    if (mappedMessage) {
      return mappedMessage;
    }

    if (error.response.status >= 500) {
      return 'Сервис промокодов временно недоступен. Попробуйте ещё раз.';
    }
  }

  if (error instanceof Error) {
    const mappedMessage = mapPromoActivationMessage(undefined, error.message);
    if (mappedMessage) {
      return mappedMessage;
    }
  }

  return 'Не удалось применить промокод. Проверьте код и попробуйте ещё раз.';
}

export function PaywallPage() {
  const { clubSlug } = useParams<{ clubSlug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const runtime = useAuthStore((s) => s.runtime);
  const authUser = useAuthStore((s) => s.user);
  const [payerPhone, setPayerPhone] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [activatedPromoCode, setActivatedPromoCode] = useState<string | null>(null);
  const [promoPreview, setPromoPreview] = useState<{
    originalAmount: number;
    discountAmount: number;
    finalAmount: number;
    currency: string;
  } | null>(null);
  const [promoMessage, setPromoMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isActivatingPromo, setIsActivatingPromo] = useState(false);
  const source = searchParams.get('source') === 'my-clubs' ? 'my-clubs' : 'catalog';
  const demoContext = getDemoContext(searchParams);
  const legalReturnTo = `${location.pathname}${location.search}${location.hash}`;
  const authQueryScope = useMemo(
    () => `${runtime}:${authUser?.id ?? 'anonymous'}`,
    [authUser?.id, runtime],
  );

  const accountEmail = useMemo(() => resolveAccountEmail(authUser), [authUser]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['paywall-offer', clubSlug, authQueryScope],
    queryFn: () => fetchPaywallOffer(clubSlug!),
    ...activeClubContentFreshnessOptions,
    enabled: !!clubSlug,
  });

  const clubHref = data ? buildClubHref(data.club, source) : '/catalog';
  const onBack = useCallback(() => {
    void navigate(clubHref);
  }, [clubHref, navigate]);
  useTelegramBackButton(onBack, true);

  if (isLoading) {
    return (
      <PageFrame
        container="adaptive-app"
        topInset="stack"
        bottomInset="none"
        className="space-y-4 pb-6"
      >
        <StackHeader onBack={onBack} label="Покупка" />
        <NeutralLoadingState rows={4} ariaLabel="Загрузка предложения" />
      </PageFrame>
    );
  }

  if (error || !data) {
    return (
      <PageFrame
        container="adaptive-app"
        topInset="stack"
        bottomInset="none"
        className="space-y-4 pb-6"
      >
        <StackHeader onBack={onBack} label="Покупка" />
        <ErrorState
          title="Не удалось загрузить"
          description="Попробуйте вернуться и открыть страницу снова."
          onRetry={onBack}
          retryLabel="Назад"
        />
      </PageFrame>
    );
  }

  const { club, hasDemo, purchase } = data;
  const requiresEmailAuthForPurchase =
    runtime === 'telegram' &&
    !accountEmail &&
    data.accessStatus === 'locked' &&
    Boolean(club.price) &&
    Boolean(club.paywallSlug);
  const canSubmitPurchase = purchase.canPurchase && Boolean(accountEmail);

  if (data.accessStatus === 'owned') {
    return <Navigate to={clubHref} replace />;
  }

  async function handlePurchaseSubmit() {
    if (!clubSlug || !club.price) {
      setErrorMessage('Клуб сейчас недоступен для покупки.');
      return;
    }

    if (!accountEmail) {
      setErrorMessage('Для покупки нужен verified email в аккаунте.');
      return;
    }

    if (payerPhone.trim().length < 5) {
      setErrorMessage('Введите номер телефона для оплаты.');
      return;
    }

    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      const response = await createClubPurchaseOrder(clubSlug, {
        payerEmail: accountEmail,
        payerPhone: payerPhone.trim(),
        ...(activatedPromoCode ? { promoCode: activatedPromoCode } : {}),
      });

      if (response.outcome === 'already_owned') {
        void navigate(clubHref, { replace: true });
        return;
      }

      trackYandexGoal('purchase_intent_submit', {
        club_id: response.order.clubId,
        club_slug: response.order.paywallSlug ?? clubSlug,
        source_surface: 'paywall',
        demo_context: demoContext ?? undefined,
        has_demo: hasDemo,
      });
      rememberPurchaseAttempt({
        orderId: response.order.id,
        clubId: response.order.clubId,
        paywallSlug: response.order.paywallSlug ?? clubSlug,
        source,
        chapterId: null,
        demoContext,
        createdAt: new Date().toISOString(),
      });
      submitHostedPaymentSession(response.paymentSession);
    } catch (submitError) {
      setErrorMessage(await formatPaymentError(submitError));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handlePromoActivate() {
    const code = promoCode.trim();
    if (!clubSlug || !code) return;

    setErrorMessage(null);
    setPromoMessage(null);
    setIsActivatingPromo(true);

    try {
      const response = await activateClubPromoCode(clubSlug, { code });
      setActivatedPromoCode(response.promoCode);
      setPromoPreview({
        originalAmount: response.originalAmount,
        discountAmount: response.discountAmount,
        finalAmount: response.finalAmount,
        currency: response.currency,
      });

      if (response.grantsAccessImmediately) {
        setPromoMessage('Промокод активирован. Доступ открыт.');
        void navigate(clubHref, { replace: true });
        return;
      }

      setPromoMessage('Промокод активирован. Цена пересчитана.');
    } catch (activationError) {
      setActivatedPromoCode(null);
      setPromoPreview(null);
      setPromoMessage(await formatPromoActivationError(activationError));
    } finally {
      setIsActivatingPromo(false);
    }
  }

  function handleEmailAuthRedirect() {
    if (!clubSlug) {
      return;
    }

    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    rememberAuthIntent(returnTo);
    void navigate(buildAuthHref(returnTo));
  }

  const benefitsFooter = club.price ? (
    <ClubPurchaseInfo price={club.price} hasDemo={hasDemo} />
  ) : null;
  const displayPrice = promoPreview;
  const formatPrice = (amount: number, currency: string) =>
    new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);

  return (
    <PageFrame
      container="adaptive-app"
      topInset="stack"
      bottomInset="none"
      className="space-y-4 pb-6"
    >
      <StackHeader onBack={onBack} label="Покупка" />
      <ClubHero
        title={club.title}
        bookAuthor={club.bookAuthor}
        host={club.host}
        secondHost={club.secondHost}
        coverUrl={club.coverUrl}
        description={club.shortDescription}
        accessStatus="locked"
        primaryActionLabel="Купить клуб"
        showPrimaryAction={false}
        badge={
          <span className="inline-flex items-center rounded-full bg-[rgba(26,26,26,0.08)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-[rgba(26,26,26,0.64)]">
            Закрытый клуб
          </span>
        }
        footer={benefitsFooter}
      />

      <MiniappSurface className="space-y-4 px-5 py-6 min-[768px]:mx-auto min-[768px]:w-full min-[768px]:max-w-[var(--content-readable-max-width)]">
        <div className="space-y-1">
          <h2 className="text-[18px] font-black uppercase tracking-[0.02em] text-[var(--foreground)]">
            Покупка клуба
          </h2>
          <p className="text-[13px] leading-5 text-[rgba(26,26,26,0.62)]">
            Оплата привяжется к текущему аккаунту. Доступ открывается только после подтверждённого
            платежа.
          </p>
        </div>

        {requiresEmailAuthForPurchase ? (
          <>
            <p className="rounded-[16px] bg-[rgba(232,93,42,0.1)] px-4 py-3 text-[13px] leading-5 text-[rgba(123,49,21,0.92)]">
              Чтобы оплатить клуб, сначала войдите по email и привяжите его к этому аккаунту. После
              входа мы вернём вас к покупке.
            </p>

            <div className="space-y-2">
              <PrimaryButton
                onClick={handleEmailAuthRedirect}
                className="w-full"
                disabled={isSubmitting}
              >
                Войти по email
              </PrimaryButton>

              {hasDemo && (
                <PrimaryButton
                  tone="ghost"
                  onClick={() => void navigate(clubHref)}
                  className="w-full"
                  disabled={isSubmitting}
                >
                  Открыть демо
                </PrimaryButton>
              )}
            </div>
          </>
        ) : (
          <>
            <label className="block space-y-2">
              <span className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[rgba(26,26,26,0.48)]">
                Email аккаунта
              </span>
              <input
                aria-label="Email аккаунта"
                type="email"
                value={accountEmail}
                readOnly
                disabled
                className="h-12 w-full rounded-[18px] border border-[rgba(26,26,26,0.08)] bg-[rgba(255,255,255,0.82)] px-4 text-[14px] text-[rgba(26,26,26,0.72)] outline-none"
              />
            </label>

            <div className="space-y-2">
              <label className="block space-y-2">
                <span className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[rgba(26,26,26,0.48)]">
                  Промокод
                </span>
                <input
                  aria-label="Промокод"
                  value={promoCode}
                  onChange={(event) => {
                    setPromoCode(event.target.value);
                    setActivatedPromoCode(null);
                    setPromoPreview(null);
                    setPromoMessage(null);
                  }}
                  disabled={!canSubmitPurchase || isSubmitting || isActivatingPromo}
                  maxLength={40}
                  className="h-12 w-full rounded-[18px] border border-[rgba(26,26,26,0.08)] bg-[rgba(255,255,255,0.82)] px-4 text-[14px] outline-none transition focus:border-[rgba(26,26,26,0.22)] disabled:text-[rgba(26,26,26,0.42)]"
                />
              </label>
              {promoCode.trim() ? (
                <PrimaryButton
                  tone="ghost"
                  onClick={() => void handlePromoActivate()}
                  className="w-full"
                  disabled={!canSubmitPurchase || isSubmitting || isActivatingPromo}
                >
                  {isActivatingPromo ? 'Проверяем…' : 'Активировать'}
                </PrimaryButton>
              ) : null}
            </div>

            {displayPrice ? (
              <div className="rounded-[16px] bg-[rgba(26,26,26,0.04)] px-4 py-3 text-[13px] leading-5 text-[rgba(26,26,26,0.72)]">
                <div className="flex items-center justify-between gap-3">
                  <span>Стоимость</span>
                  <span className="font-semibold">
                    {formatPrice(displayPrice.originalAmount, displayPrice.currency)}
                  </span>
                </div>
                {displayPrice.discountAmount > 0 ? (
                  <>
                    <div className="flex items-center justify-between gap-3 text-[rgba(26,26,26,0.58)]">
                      <span>Скидка</span>
                      <span>
                        -{formatPrice(displayPrice.discountAmount, displayPrice.currency)}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-3 text-[15px] font-black text-[var(--foreground)]">
                      <span>К оплате</span>
                      <span>{formatPrice(displayPrice.finalAmount, displayPrice.currency)}</span>
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}

            {promoMessage && (
              <p className="rounded-[16px] bg-[rgba(37,99,235,0.08)] px-4 py-3 text-[13px] leading-5 text-[rgba(30,64,175,0.92)]">
                {promoMessage}
              </p>
            )}

            <label className="block space-y-2">
              <span className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[rgba(26,26,26,0.48)]">
                Телефон
              </span>
              <input
                aria-label="Телефон"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={payerPhone}
                onChange={(event) => setPayerPhone(event.target.value)}
                disabled={!canSubmitPurchase || isSubmitting}
                placeholder="+7 999 123-45-67"
                className="h-12 w-full rounded-[18px] border border-[rgba(26,26,26,0.08)] bg-[rgba(255,255,255,0.82)] px-4 text-[14px] outline-none transition focus:border-[rgba(26,26,26,0.22)] disabled:text-[rgba(26,26,26,0.42)]"
              />
            </label>

            {!purchase.canPurchase && (
              <p className="rounded-[16px] bg-[rgba(232,93,42,0.1)] px-4 py-3 text-[13px] leading-5 text-[rgba(123,49,21,0.92)]">
                Клуб сейчас недоступен для покупки.
              </p>
            )}

            {!accountEmail && (
              <p className="rounded-[16px] bg-[rgba(232,93,42,0.1)] px-4 py-3 text-[13px] leading-5 text-[rgba(123,49,21,0.92)]">
                В аккаунте не найден verified email. Войдите по email и попробуйте снова.
              </p>
            )}

            {errorMessage && (
              <p className="rounded-[16px] bg-[rgba(232,93,42,0.1)] px-4 py-3 text-[13px] leading-5 text-[rgba(123,49,21,0.92)]">
                {errorMessage}
              </p>
            )}

            <div className="space-y-2">
              <PrimaryButton
                onClick={() => void handlePurchaseSubmit()}
                className="w-full"
                disabled={!canSubmitPurchase || isSubmitting}
              >
                {isSubmitting ? 'Переходим к оплате…' : 'Перейти к оплате'}
              </PrimaryButton>

              {hasDemo && (
                <PrimaryButton
                  tone="ghost"
                  onClick={() => void navigate(clubHref)}
                  className="w-full"
                  disabled={isSubmitting}
                >
                  Открыть демо
                </PrimaryButton>
              )}
            </div>

            <div className="space-y-2 border-t border-[rgba(26,26,26,0.06)] pt-4">
              <p className="text-[12px] leading-5 text-[rgba(26,26,26,0.52)]">
                Продолжая, вы соглашаетесь с{' '}
                <button
                  type="button"
                  onClick={() => void navigate(buildLegalHref('/legal/offer', legalReturnTo))}
                  className="font-medium text-[var(--accent)] underline underline-offset-2"
                >
                  публичной офертой
                </button>{' '}
                и{' '}
                <button
                  type="button"
                  onClick={() => void navigate(buildLegalHref('/legal/privacy', legalReturnTo))}
                  className="font-medium text-[var(--accent)] underline underline-offset-2"
                >
                  политикой обработки персональных данных
                </button>{' '}
                и{' '}
                <button
                  type="button"
                  onClick={() => void navigate(buildLegalHref('/legal/payment', legalReturnTo))}
                  className="font-medium text-[var(--accent)] underline underline-offset-2"
                >
                  условиями оплаты и возврата
                </button>
                .
              </p>
            </div>
          </>
        )}
      </MiniappSurface>
    </PageFrame>
  );
}
